import { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import delay from '@zenbusiness/application-commons-utils/delay';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import PageLoading from '@zenbusiness/zui/PageLoading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { NOT_SUBMITTABLE_APPLICATION_STATUSES } from '#client/components/ApplicationReceivedModal/constants/application';
import FormStepModal from '#client/components/FormStepModal';
import { ContainerGrid, FocusGrid } from '#client/components/Grid';
import ActionButtonsGrid from '#client/components/Grid/ActionButtonsGrid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import StepInfo from '#client/components/StepInfo';
import { RELEASE_SURVEY_BANKING_APPLICATION_FLOW } from '#client/constants/experimentsFlags';
import { BANKING_APPLICATION_SUCCESS_SURVEY } from '#client/constants/hotjarEvents';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useInitHotJar, { useHotJarEvent } from '#client/hooks/useHotJar';
import useToastContext from '#client/hooks/useToastContext';
import { PROCESS_APPLICATION_DATA_MUTATION } from '#client/mutations';

import { ContentWrapper, ImageWrapper, PaperStyled } from './ApplicationReceivedModal.styles';

import { ALLOW_LIST_ERROR_MESSAGE, ALLOW_LIST_SUCCESS } from './constants/messages';
import { getEINBoxByErrorMessage } from './utils/getEIN';

const ApplicationReceivedModal = ({ handleClose }) => {
  const navigate = useNavigate();
  const dashboardPath = useDashboardPath();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  useInitHotJar();

  const captureException = useCaptureException();
  const { businessEntityUuid, loading: loadingBusinessEntity } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const [enableBankingApplicationFlowSurvey, loadingEnableBankingApplicationFlowSurvey] =
    useFeatureFlag(RELEASE_SURVEY_BANKING_APPLICATION_FLOW);
  const { appStatus } = useApplicationStatusContext();
  const sendHotjarEvent = useHotJarEvent();
  const [processApplicationData, , { loading: loadingProcessApplicationData }] = useMutation(
    PROCESS_APPLICATION_DATA_MUTATION,
    {
      noThrow: true
    }
  );

  const [einBox, setEinBox] = useState(null);

  const handleCommonErrorToast = (error) => {
    createCommonErrorToast({ error });
    handleClose();
  };

  // trigger API process when we have businessEntityUuid and applicationId
  useEffect(() => {
    const submitHandler = async () => {
      const { data, error } = await processApplicationData({
        variables: {
          input: {
            businessUuid: businessEntityUuid
          }
        }
      });

      if (error) {
        // Display a common error toast and stop execution
        handleCommonErrorToast(error);
        return;
      }

      // Check if the application data processing was successful
      if (data?.processApplicationData?.success) {
        // Navigate to the application submission modal
        navigate(`?modal=${MODAL_NAMES.APPLICATION_SUBMISSION_MODAL}`, { replace: true });
        return;
      }

      const { message } = data?.processApplicationData || {};
      const einContentBox = getEINBoxByErrorMessage(message);
      setEinBox(einContentBox);

      // Check if the message is valid based on an allow list
      if (ALLOW_LIST_SUCCESS.includes(message)) {
        return;
      }
      if (ALLOW_LIST_ERROR_MESSAGE.includes(message)) {
        captureException(
          new Error(
            `Application submission failed with businessEntityUuid: ${businessEntityUuid}. Message: ${message}.`
          ),
          {
            level: 'warning'
          }
        );
        handleCommonErrorToast();
      }

      handleClose();
    };

    const notSubmittableAppStatus =
      appStatus && NOT_SUBMITTABLE_APPLICATION_STATUSES.includes(appStatus);
    if (notSubmittableAppStatus) {
      handleClose();
    }

    if (!loadingBusinessEntity && businessEntityUuid && !notSubmittableAppStatus) {
      // Trigger the submit handler
      submitHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingBusinessEntity, businessEntityUuid, appStatus]);

  const stillShowLoadingGif = loadingBusinessEntity || loadingProcessApplicationData || !einBox;
  const modalConfig = useMemo(
    () => ({
      title: stillShowLoadingGif ? '' : 'Finish your formation',
      disableCloseModal: stillShowLoadingGif,
      stepNames: stillShowLoadingGif ? [] : ['1', '2', '3', '4'], // This is just a mock to render progress bar same with the design
      currentStep: stillShowLoadingGif ? '' : '4'
    }),
    [stillShowLoadingGif]
  );

  // Send hotjar event for banking application flow survey
  const sendHotjarEventHandler = () => {
    if (enableBankingApplicationFlowSurvey && !loadingEnableBankingApplicationFlowSurvey) {
      delay(() => {
        sendHotjarEvent(BANKING_APPLICATION_SUCCESS_SURVEY);
      }, 1000);
    }
  };

  // redirect to Zendash dashboard
  const handleNext = () => {
    if (einBox.REDIRECT) {
      navigate(dashboardPath);
    } else {
      /**
       * The behavior is as follows:
       * When the modal application is closed, the user is redirected back to the dashboard.
       * After that, some APIs are loaded if necessary.
       * To ensure stability and prevent potential DOM-related issues or other unpredictable behavior, I’ve added a 1-second delay.
       */
      sendHotjarEventHandler();
      handleClose();
    }
  };

  return (
    <FormStepModal
      currentStep={modalConfig.currentStep}
      disableCloseModal={modalConfig.disableCloseModal}
      handleClose={() => {
        sendHotjarEventHandler();
        handleClose();
      }}
      isHideBackButton
      stepNames={modalConfig.stepNames}
      title={modalConfig.title}
    >
      {stillShowLoadingGif || !einBox ? (
        <PageLoading />
      ) : (
        <>
          <DialogContent sx={{ padding: 0 }}>
            <ContainerGrid padding="48px 0">
              <FocusGrid>
                <ImageWrapper
                  aria-label="application received image"
                  textAlign={isDesktop ? 'end' : 'center'}
                >
                  <SharedAsset
                    alt=""
                    src="/illustration/banking-application-received.svg"
                    width={isDesktop ? 469 : 120}
                  />
                </ImageWrapper>
                <ContentWrapper>
                  <Box mb="16px">
                    <Heading
                      family="secondary"
                      size="3xl"
                      weight="bold"
                    >
                      {einBox.TITLE}
                    </Heading>
                  </Box>
                  <Text
                    color="primary"
                    size="lg"
                  >
                    {einBox.DESCRIPTION}
                  </Text>
                  {einBox?.EIN_BOX && (
                    <PaperStyled
                      aria-label="EIN box"
                      elevation={0}
                      variant="outlined"
                    >
                      <Heading
                        sx={{
                          fontWeight: 400
                        }}
                      >
                        {einBox?.EIN_BOX?.TITLE}
                      </Heading>
                      <Stack mt="32px">
                        {einBox?.EIN_BOX?.DESCRIPTIONS.map(({ title, description }, index) => (
                          <StepInfo
                            description={description}
                            key={title}
                            stepIndex={index + 1}
                            title={title}
                          />
                        ))}
                      </Stack>
                      {einBox?.EIN_BOX?.SUB_DESCRIPTION && (
                        <Heading
                          aria-label="sub description"
                          color="tertiary"
                          size="lg"
                        >
                          {einBox.EIN_BOX.SUB_DESCRIPTION}
                        </Heading>
                      )}
                    </PaperStyled>
                  )}
                </ContentWrapper>
              </FocusGrid>
            </ContainerGrid>
          </DialogContent>
          <ActionButtonsGrid type={ACTION_BUTTON_TYPE.FULL_WIDTH}>
            <Button
              aria-label="Advance to next step"
              fullWidth={!isDesktop}
              onClick={handleNext}
              size="large"
            >
              {einBox.CTA_TEXT}
            </Button>
          </ActionButtonsGrid>
        </>
      )}
    </FormStepModal>
  );
};

ApplicationReceivedModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default ApplicationReceivedModal;
